import { useEffect, useState, useCallback } from 'react';
import { useEspot } from '@app/hooks';
import cx from 'classnames';
import { EmbeddedVideo } from 'cat-ecommerce-alloy';
import { v4 } from 'uuid';
import VideoWarning from '../../../../common/VideoWarning/VideoWarning';
import Espot from '@app/components/common/Espot/Espot';
import styles from './youtubeWidget.module.scss';
import {
  CONTENT_ALIGNMENT,
  HALF_WIDTH_CLASSNAME,
  FULL_WIDTH_CLASSNAME
} from './constants';
import PropTypes from 'prop-types';
import { isConsentTypeAvailable } from '@app/utils';
import { ONETRUST } from '@app/constants/commonConstants';

const YoutubeWidget = ({ widgetProps }) => {
  const { emsName } = widgetProps;
  const { espotData } = useEspot(emsName);
  const [widgetLayout, setWidgetLayout] = useState([]);
  const [isCookiesAccepted, setIsCookiesAccepted] = useState(
    isConsentTypeAvailable(ONETRUST.TARGET_COOKIES_ACCEPTED)
  );
  const videoUrls = espotData[0] && espotData[0][emsName].properties[0].url;

  const contentAlignment =
    espotData[0] && espotData[0][emsName].properties[0].contentAlignment;
  const contents = espotData[0] && espotData[0][emsName].contents;
  const urlArray = videoUrls?.split(';');
  const widthAndAlignment =
    contentAlignment === CONTENT_ALIGNMENT.LEFT ||
    contentAlignment === CONTENT_ALIGNMENT.RIGHT
      ? HALF_WIDTH_CLASSNAME
      : FULL_WIDTH_CLASSNAME;

  const getEmbedVideoRenderData = useCallback(
    url => {
      const contentAlignDirection =
        contentAlignment === CONTENT_ALIGNMENT.LEFT ||
        contentAlignment === CONTENT_ALIGNMENT.RIGHT ||
        urlArray?.length > 1
          ? styles.video_height_half_width
          : styles.video_height_full_width;
      return {
        id: v4(),
        component: isCookiesAccepted ? EmbeddedVideo : VideoWarning,
        class: isCookiesAccepted
          ? widthAndAlignment
          : `${widthAndAlignment} position-relative ${styles.warning_message}`,
        props: {
          url,
          className: isCookiesAccepted
            ? contentAlignDirection
            : `${styles.warning_width}`
        }
      };
    },
    [isCookiesAccepted, contentAlignment, videoUrls]
  );

  const getEspotRenderData = useCallback(
    contents => {
      return {
        id: v4(),
        component: Espot,
        class: widthAndAlignment,
        props: {
          html: { contents }
        }
      };
    },
    [contentAlignment]
  );

  const getLayoutData = useCallback(
    urlList => {
      const layoutData = [];
      urlList.forEach((item, index) => {
        const rowContainer = {
          id: v4(),
          class:
            urlList.length > 1 ? HALF_WIDTH_CLASSNAME : FULL_WIDTH_CLASSNAME,
          children: []
        };
        if (
          contentAlignment === CONTENT_ALIGNMENT.TOP ||
          contentAlignment === CONTENT_ALIGNMENT.LEFT
        ) {
          rowContainer.children.push(getEspotRenderData(contents[index]));
          rowContainer.children.push(getEmbedVideoRenderData(item));
        } else if (
          contentAlignment === CONTENT_ALIGNMENT.BOTTOM ||
          contentAlignment === CONTENT_ALIGNMENT.RIGHT
        ) {
          rowContainer.children.push(getEmbedVideoRenderData(item));
          rowContainer.children.push(getEspotRenderData(contents[index]));
        } else {
          rowContainer.children.push(getEmbedVideoRenderData(item));
        }
        layoutData.push(rowContainer);
      });
      return layoutData;
    },
    [contentAlignment, contents, getEmbedVideoRenderData, getEspotRenderData]
  );

  useEffect(() => {
    urlArray?.length && setWidgetLayout(getLayoutData(urlArray));
  }, [videoUrls, getLayoutData]);

  useEffect(() => {
    window.addEventListener('OneTrustGroupsUpdated', () => {
      const consentTypeAvailable = isConsentTypeAvailable(
        ONETRUST.TARGET_COOKIES_ACCEPTED
      );
      if (consentTypeAvailable) {
        setIsCookiesAccepted(consentTypeAvailable);
      }
    });
  }, [widgetLayout]);

  return (
    <div className={cx(`${styles['youtube-widget--container']} row`)}>
      <div className={cx(`container`)}>
        <div className={cx(`row`)}>
          {widgetLayout.map(item => {
            return (
              <div
                key={item.id}
                data-testid="section-container"
                className={`pt-1 pt-md-3 mb-2 ${item.class}`}
              >
                {item.children.map(elem => {
                  const Comp = elem.component;
                  return (
                    <div
                      key={elem.id}
                      data-testid="component-container"
                      className={`mb-2 px-3 ${styles['youtube-widget--children']} ${elem.class}`}
                    >
                      <Comp {...elem.props} />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

YoutubeWidget.propTypes = {
  widgetProps: PropTypes.shape({
    emsName: PropTypes.string
  })
};

export default YoutubeWidget;
